import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  FaEnvelope,
  FaIdBadge,
  FaMobileAlt,
  FaStore,
  FaUserTag,
  FaVolumeUp,
} from "react-icons/fa";
import { SESSION_UPDATED_EVENT } from "../utils/accountLinks";
import { getStoredUser } from "../utils/impersonation";
import { IMAGE_BASE_URL } from "../config/constants";

const readUser = () => getStoredUser() || {};

const resolvePhotoUrl = (user = {}) => {
  const raw =
    user?.staff_profile?.photo ||
    user?.photo ||
    user?.image ||
    user?.avatar ||
    user?.avatar_url ||
    "";
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${IMAGE_BASE_URL}${String(raw).replace(/^\/+/, "")}`;
};

const flagLabel = (value) => (Number(value) === 1 ? "On" : "Off");

const WaiterProfilePage = () => {
  const [user, setUser] = useState(readUser);

  useEffect(() => {
    const refresh = () => setUser(readUser());
    window.addEventListener(SESSION_UPDATED_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(SESSION_UPDATED_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const profile = useMemo(
    () => ({
      name:
        user?.staffname ||
        user?.staff_profile?.name ||
        user?.name ||
        "Waiter",
      restaurant:
        user?.restaurant_name ||
        user?.restaurant_profile?.restaurant_name ||
        "Restaurant",
      email:
        user?.staffemail ||
        user?.staff_profile?.email ||
        user?.email ||
        "-",
      phone:
        user?.staffphone ||
        user?.staff_profile?.phone ||
        user?.mobile_number ||
        "-",
      staffId:
        user?.staffid ||
        user?.staff_profile?.id ||
        "-",
      role:
        user?.staffrole ||
        user?.staff_profile?.role ||
        user?.role ||
        "waiter",
      sound: flagLabel(user?.issoundenable),
      vibration: flagLabel(user?.isvibrationenable),
      photoUrl: resolvePhotoUrl(user),
    }),
    [user]
  );

  return (
    <Page>
      <Hero>
        <AvatarWrap>
          {profile.photoUrl ? (
            <Avatar src={profile.photoUrl} alt={profile.name} />
          ) : (
            <AvatarFallback>
              {profile.name.charAt(0).toUpperCase()}
            </AvatarFallback>
          )}
        </AvatarWrap>
        <HeroCopy>
          <Eyebrow>Staff Profile</Eyebrow>
          <Title>{profile.name}</Title>
          <Subtitle>{profile.restaurant}</Subtitle>
          <RoleBadge>{String(profile.role).replace(/_/g, " ")}</RoleBadge>
        </HeroCopy>
      </Hero>

      <Grid>
        <InfoCard>
          <CardLabel>
            <FaIdBadge />
            <span>Staff ID</span>
          </CardLabel>
          <CardValue>{profile.staffId}</CardValue>
        </InfoCard>
        <InfoCard>
          <CardLabel>
            <FaMobileAlt />
            <span>Phone</span>
          </CardLabel>
          <CardValue>{profile.phone}</CardValue>
        </InfoCard>
        <InfoCard>
          <CardLabel>
            <FaEnvelope />
            <span>Email</span>
          </CardLabel>
          <CardValue>{profile.email}</CardValue>
        </InfoCard>
        <InfoCard>
          <CardLabel>
            <FaStore />
            <span>Restaurant</span>
          </CardLabel>
          <CardValue>{profile.restaurant}</CardValue>
        </InfoCard>
      </Grid>

      <Panel>
        <PanelTitle>Device Preferences</PanelTitle>
        <PrefRow>
          <PrefItem>
            <CardLabel>
              <FaVolumeUp />
              <span>Sound</span>
            </CardLabel>
            <CardValue>{profile.sound}</CardValue>
          </PrefItem>
          <PrefItem>
            <CardLabel>
              <FaUserTag />
              <span>Vibration</span>
            </CardLabel>
            <CardValue>{profile.vibration}</CardValue>
          </PrefItem>
        </PrefRow>
      </Panel>
    </Page>
  );
};

export default WaiterProfilePage;

const Page = styled.div`
  min-height: 100vh;
  padding: 24px;
  background:
    radial-gradient(circle at top left, rgba(14, 165, 233, 0.16), transparent 28%),
    linear-gradient(180deg, #f8fbff 0%, #eef4fb 100%);
  display: grid;
  gap: 20px;
`;

const Hero = styled.section`
  background: linear-gradient(135deg, #082f49 0%, #0f5ea7 100%);
  color: #f8fafc;
  border-radius: 22px;
  padding: 28px;
  display: flex;
  gap: 18px;
  align-items: center;
  box-shadow: 0 28px 50px rgba(8, 47, 73, 0.24);

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const AvatarWrap = styled.div`
  flex-shrink: 0;
`;

const Avatar = styled.img`
  width: 88px;
  height: 88px;
  border-radius: 24px;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.26);
`;

const AvatarFallback = styled.div`
  width: 88px;
  height: 88px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.14);
  border: 2px solid rgba(255, 255, 255, 0.22);
  display: grid;
  place-items: center;
  font-size: 30px;
  font-weight: 800;
`;

const HeroCopy = styled.div`
  display: grid;
  gap: 6px;
`;

const Eyebrow = styled.div`
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.76;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(28px, 4vw, 38px);
  line-height: 1;
`;

const Subtitle = styled.div`
  font-size: 15px;
  opacity: 0.9;
`;

const RoleBadge = styled.div`
  width: fit-content;
  margin-top: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  text-transform: capitalize;
  font-size: 13px;
  font-weight: 700;
`;

const Grid = styled.section`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const InfoCard = styled.article`
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.06);
  display: grid;
  gap: 10px;
`;

const CardLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #475569;
  font-size: 13px;
  font-weight: 700;
`;

const CardValue = styled.div`
  color: #0f172a;
  font-size: 18px;
  font-weight: 700;
  word-break: break-word;
`;

const Panel = styled.section`
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 18px;
  padding: 22px;
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.06);
  display: grid;
  gap: 16px;
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  color: #0f172a;
`;

const PrefRow = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const PrefItem = styled.div`
  padding: 16px;
  border-radius: 16px;
  background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
  border: 1px solid rgba(148, 163, 184, 0.2);
  display: grid;
  gap: 10px;
`;
